import React from 'react';
import { Alert, Col, Collapse, Row, Tag, Descriptions } from 'antd';
import moment from 'moment';
import { ExtractedContractDto } from 'services/contracts/extracted-contract.dto';

const { Panel } = Collapse;

interface ContractReviewCardProps {
    data: ExtractedContractDto;
}

const fmt = (val: string | number | null): string => (val !== null && val !== undefined ? String(val) : '-');

const computeFinishDate = (data: ExtractedContractDto['contract']): string => {
    if (data.finishDate) return data.finishDate;
    if (data.initialDate && data.durationMonths) {
        return moment(data.initialDate).add(data.durationMonths, 'months').format('YYYY-MM-DD');
    }
    return '-';
};

export const ContractReviewCard: React.FC<ContractReviewCardProps> = ({ data }) => {
    const hasWarnings = data.warnings.length > 0;
    const hasMatches =
        data.property.existingId !== null ||
        data.renter.existingId !== null ||
        data.owner.existingId !== null ||
        (data.guarantor !== null && data.guarantor.existingId !== null);

    const headerExtra = (
        <span>
            {hasWarnings && (
                <Tag color="warning" style={{ marginRight: 4 }}>
                    {data.warnings.length} advertencia{data.warnings.length > 1 ? 's' : ''}
                </Tag>
            )}
            {hasMatches && <Tag color="success">Coincidencias encontradas</Tag>}
        </span>
    );

    const personDesc = (person: ExtractedContractDto['renter'] | ExtractedContractDto['owner'], label: string) => (
        <Descriptions
            title={
                <span>
                    {label}
                    {person.existingId !== null && (
                        <Tag color="success" style={{ marginLeft: 8 }}>
                            Ya existe (ID {person.existingId})
                        </Tag>
                    )}
                </span>
            }
            size="small"
            column={2}
            style={{ marginBottom: 16 }}
        >
            <Descriptions.Item label="Nombre">{fmt(person.name)}</Descriptions.Item>
            <Descriptions.Item label="Apellido">{fmt(person.lastName)}</Descriptions.Item>
            <Descriptions.Item label="DNI">{fmt(person.dni)}</Descriptions.Item>
            <Descriptions.Item label="Teléfono">{fmt(person.phone)}</Descriptions.Item>
            <Descriptions.Item label="Email">{fmt(person.email)}</Descriptions.Item>
        </Descriptions>
    );

    if (!data) return null;

    return (
        <Collapse style={{ marginBottom: 12 }} defaultActiveKey={['1']}>
            <Panel
                header={
                    <Row justify="space-between" align="middle">
                        <Col>
                            <strong>{data.fileName}</strong>
                        </Col>
                        <Col>{headerExtra}</Col>
                    </Row>
                }
                key="1"
            >
                {/* Property */}
                <Descriptions
                    title={
                        <span>
                            Inmueble
                            {data.property.existingId !== null && (
                                <Tag color="success" style={{ marginLeft: 8 }}>
                                    Ya existe (ID {data.property.existingId})
                                </Tag>
                            )}
                        </span>
                    }
                    size="small"
                    column={2}
                    style={{ marginBottom: 16 }}
                >
                    <Descriptions.Item label="Calle">{fmt(data.property.street)}</Descriptions.Item>
                    <Descriptions.Item label="Número">{fmt(data.property.number)}</Descriptions.Item>
                    <Descriptions.Item label="Piso/Depto">{fmt(data.property.floor)}</Descriptions.Item>
                    <Descriptions.Item label="Ciudad">{fmt(data.property.city)}</Descriptions.Item>
                    <Descriptions.Item label="Barrio">{fmt(data.property.district)}</Descriptions.Item>
                </Descriptions>

                {/* Persons */}
                {personDesc(data.owner, 'Propietario')}
                {personDesc(data.renter, 'Inquilino')}
                {data.guarantor && personDesc(data.guarantor, 'Garante')}

                {/* Contract data */}
                <Descriptions title="Contrato" size="small" column={2} style={{ marginBottom: 16 }}>
                    <Descriptions.Item label="Precio inicial">
                        {data.contract.startPrice !== null
                            ? `$${data.contract.startPrice.toLocaleString('es-AR')}`
                            : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Fecha de inicio">{fmt(data.contract.initialDate)}</Descriptions.Item>
                    <Descriptions.Item label="Duración (meses)">{fmt(data.contract.durationMonths)}</Descriptions.Item>
                    <Descriptions.Item label="Fecha de fin">{computeFinishDate(data.contract)}</Descriptions.Item>
                    <Descriptions.Item label="Actualización (meses)">
                        {fmt(data.contract.updateFrequency)}
                    </Descriptions.Item>
                    <Descriptions.Item label="% Administración">
                        {fmt(data.contract.percentageByAdministration)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Día de vencimiento">{fmt(data.contract.expiredDay)}</Descriptions.Item>
                </Descriptions>

                {/* Warnings */}
                {data.warnings.map((w, i) => (
                    <Alert key={i} type="warning" message={w} showIcon style={{ marginBottom: 8 }} />
                ))}
            </Panel>
        </Collapse>
    );
};
